import clsx from 'clsx';
import { RotateCcw, RotateCw, Undo2, Redo2, X } from 'lucide-react';
import type { Bucket, MediaItem } from '../../types';
import { Kbd } from './Primitives';

export default function BucketBar({
  buckets,
  current,
  canUndo,
  canRedo,
  autoAdvance,
  onAssign,
  onClear,
  onRotate,
  onUndo,
  onRedo,
  onToggleAutoAdvance,
}: {
  buckets: Bucket[];
  current: MediaItem | null;
  canUndo: boolean;
  canRedo: boolean;
  autoAdvance: boolean;
  onAssign: (bucket: Bucket) => void;
  onClear: () => void;
  onRotate: (degrees: number) => void;
  onUndo: () => void;
  onRedo: () => void;
  onToggleAutoAdvance: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 border-t border-ink-850 bg-ink-900 px-3 py-2.5">
      {buckets.map((bucket) => {
        const active = current?.bucketId === bucket.id;
        const isKeep = bucket.kind === 'keep';

        return (
          <button
            key={bucket.id}
            onClick={() => onAssign(bucket)}
            disabled={!current}
            className={clsx(
              'btn border transition',
              active && isKeep && 'border-keep-line bg-keep-soft text-keep',
              active && !isKeep && 'border-reject-line bg-reject-soft text-reject',
              !active && 'border-ink-700 text-ink-200 hover:border-ink-600 hover:bg-ink-850',
              !current && 'opacity-40',
            )}
          >
            <Kbd>{bucket.hotkey}</Kbd>
            <span>{bucket.name}</span>
            <span className="tnum text-ink-500">{bucket.count}</span>
          </button>
        );
      })}

      <button
        onClick={onClear}
        disabled={!current?.bucketId}
        className="btn-ghost"
        title="Clear this photo's bucket"
      >
        <X size={14} />
        <Kbd>0</Kbd>
      </button>

      <div className="mx-1 h-5 w-px bg-ink-800" aria-hidden="true" />

      <button onClick={() => onRotate(-90)} disabled={!current} className="btn-ghost" title="Rotate left">
        <RotateCcw size={15} />
      </button>
      <button onClick={() => onRotate(90)} disabled={!current} className="btn-ghost" title="Rotate right">
        <RotateCw size={15} />
      </button>

      <div className="mx-1 h-5 w-px bg-ink-800" aria-hidden="true" />

      <button onClick={onUndo} disabled={!canUndo} className="btn-ghost" title="Undo">
        <Undo2 size={15} />
      </button>
      <button onClick={onRedo} disabled={!canRedo} className="btn-ghost" title="Redo">
        <Redo2 size={15} />
      </button>

      <button
        onClick={onToggleAutoAdvance}
        className={clsx('btn-ghost ml-auto', autoAdvance && 'text-ink-50')}
        title="Move to the next photo automatically after a decision"
      >
        <span
          className={clsx(
            'h-2 w-2 rounded-full',
            autoAdvance ? 'bg-keep' : 'border border-ink-600',
          )}
        />
        Auto-advance
      </button>
    </div>
  );
}
